import type { GameResult, PlayerId } from "../api/types";
import type { Theme } from "../theme/pixel";
import { defaultBtn, glowShadow, panelStyle, primaryBtn } from "../theme/styles";

interface Props {
  result: GameResult;
  viewer: PlayerId;
  theme: Theme;
  onNewGame: () => void;
  buttonLabel?: string;
  /** Overrides the default "Victory" / "Defeat" / "Draw" headline.
   *  Used by spectators, who have no self-outcome — the SPA passes
   *  in something like "Alice wins" instead. */
  headlineOverride?: string;
  /** Called when the player clicks "View board". The host hides the
   *  modal and shows a "Continue" button in the action-bar slot to
   *  bring it back. Omit to hide the minimize affordance entirely. */
  onMinimize?: () => void;
}

export function GameOverBanner({
  result,
  viewer,
  theme,
  onNewGame,
  buttonLabel,
  headlineOverride,
  onMinimize,
}: Props) {
  let headline: string;
  if (headlineOverride) {
    headline = headlineOverride;
  } else if (result.kind === "draw") {
    headline = "Draw";
  } else if (result.player === viewer) {
    headline = "Victory";
  } else {
    headline = "Defeat";
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(26, 15, 5, 0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        // The play-clone, drag-clone, and FLIP overlay all use z-index
        // up to 1000. The result modal must sit above every card layer
        // — otherwise a card mid-animation can render in front of the
        // banner the instant the result lands.
        zIndex: 2000,
      }}
    >
      <div
        style={{
          ...panelStyle(theme),
          padding: "32px 48px",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          gap: 20,
          minWidth: 360,
        }}
      >
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 28,
            letterSpacing: theme.letterSpacing,
            color: theme.ink,
          }}
        >
          {headline}
        </div>

        <div
          style={{
            display: "flex",
            gap: 12,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <NewGameButton
            theme={theme}
            onClick={onNewGame}
            label={buttonLabel ?? "NEW GAME"}
          />
          {onMinimize && (
            <button
              onClick={onMinimize}
              {...defaultBtn(theme, false)}
              style={{
                ...defaultBtn(theme, false).style,
                padding: "12px 20px",
                fontSize: 12,
              }}
            >
              VIEW BOARD
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function NewGameButton({
  theme,
  onClick,
  label,
}: {
  theme: Theme;
  onClick: () => void;
  label: string;
}) {
  const p = primaryBtn(theme, false);
  return (
    <button
      onClick={onClick}
      className={p.className}
      style={{
        ...p.style,
        padding: "12px 20px",
        fontSize: 14,
        boxShadow: glowShadow(theme, theme.accentGlow, 1.4),
      }}
    >
      {label}
    </button>
  );
}
